/**
 * softphone.ts (PHONE-SOFT-01 slice 1, Seth 2026-10-01 "Proceed slice 1") —
 * the browser phone engine. One singleton per tab:
 *
 *   enroll/turn on  → softphone-token edge fn mints a Telnyx on-demand
 *                     credential for THIS login (stored server-side) and
 *                     returns a 24h login token
 *   connect         → the vendored Telnyx WebRTC bundle (public/, no CDN, no
 *                     npm step on dc2022) registers this tab as a phone
 *   inbound call    → the voice-webhook's <Dial> now carries a <Sip> leg per
 *                     enabled browser, so Telnyx rings this tab AND the desk
 *                     phones at once; first answer wins, the others stop
 *   answer/decline  → all browser-side; declining simply drops OUR leg (the
 *                     desk phones keep ringing — never steals the call)
 *
 * The dock (CallDock) renders the controls; this module owns the machine.
 * Engine loading is a runtime <script> of the vendored UMD bundle so the
 * 273KB of SIP stack never enters the main chunk and the repo needs no new
 * npm dependency (slice 2 can promote it to package.json when the Call
 * Control migration lands).
 */
import { useEffect, useSyncExternalStore } from "react";
import { supabase } from "@/lib/supabase";

// ---------------------------------------------------------------------------
// Minimal typings for the vendored bundle (window.TelnyxWebRTC).
// ---------------------------------------------------------------------------
interface TelnyxCall {
  id: string;
  state: string;
  options?: {
    remoteCallerNumber?: string;
    remoteCallerName?: string;
    callerNumber?: string;
  };
  answer: () => void;
  hangup: () => void;
  muteAudio: () => void;
  unmuteAudio: () => void;
  hold: () => Promise<unknown>;
  unhold: () => Promise<unknown>;
}
interface TelnyxClientT {
  remoteElement: HTMLMediaElement | string;
  connect: () => Promise<void>;
  disconnect: () => Promise<void> | void;
  on: (ev: string, cb: (x?: unknown) => void) => void;
  off: (ev: string) => void;
}
interface TelnyxGlobal {
  TelnyxRTC: new (opts: { login_token: string }) => TelnyxClientT;
}

export interface SoftCall {
  /** Telnyx call id (NOT our call_event id). */
  id: string;
  /** Caller's number as the invite carried it (display + dock matching). */
  from: string;
}

export interface SoftphoneState {
  /** off = not running · starting = loading/connecting · ready = registered,
   *  will ring · error = retrying/needs attention. */
  status: "off" | "starting" | "ready" | "error";
  error: string | null;
  /** Server-side enrollment (null until the first status check lands). */
  enrolled: boolean | null;
  enabled: boolean;
  ringing: SoftCall | null;
  active: SoftCall | null;
  /** Epoch ms when the active call connected (for the timer). */
  activeSince: number | null;
  muted: boolean;
  /** Caller is parked on hold (hearing Telnyx hold music). */
  held: boolean;
}

/** A place a live call can be sent (slice 2 blind transfer). */
export interface XferTarget {
  kind: "pstn" | "sip";
  value: string;
  label: string;
}

const last10 = (s: string | null | undefined) => (s ?? "").replace(/\D/g, "").slice(-10);

// ---------------------------------------------------------------------------
// The singleton
// ---------------------------------------------------------------------------
class SoftphoneManager {
  private state: SoftphoneState = {
    status: "off", error: null, enrolled: null, enabled: false,
    ringing: null, active: null, activeSince: null, muted: false, held: false,
  };
  private listeners = new Set<() => void>();
  private client: TelnyxClientT | null = null;
  private call: TelnyxCall | null = null;
  private booted = false;
  private wantedOn = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private ringCtx: AudioContext | null = null;
  private ringOsc: { stop: () => void } | null = null;

  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  get = (): SoftphoneState => this.state;
  private set(patch: Partial<SoftphoneState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  /** First mount with the capability: learn enrollment, auto-start if on. */
  boot() {
    if (this.booted) return;
    this.booted = true;
    void (async () => {
      try {
        const { data } = await supabase.functions.invoke("softphone-token", { body: { action: "status" } });
        const enrolled = !!data?.enrolled;
        const enabled = !!data?.enabled;
        this.set({ enrolled, enabled });
        if (enrolled && enabled) await this.start();
      } catch {
        this.set({ enrolled: false });
      }
    })();
  }

  /** The dock's toggle: enroll on first use, then connect + ring here. */
  async turnOn() {
    this.wantedOn = true;
    this.set({ status: "starting", error: null });
    try {
      if (this.state.enrolled && !this.state.enabled) {
        await supabase.functions.invoke("softphone-token", { body: { action: "set_enabled", enabled: true } });
      }
      await this.start(); // token action enrolls if needed
      this.set({ enrolled: true, enabled: true });
    } catch (e) {
      this.set({ status: "error", error: String((e as Error)?.message ?? e) });
    }
  }

  /** Stop ringing this browser (and tell the front door to skip it). */
  async turnOff() {
    this.wantedOn = false;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    try {
      await supabase.functions.invoke("softphone-token", { body: { action: "set_enabled", enabled: false } });
    } catch { /* the disconnect below still stops this tab */ }
    this.teardown();
    this.set({ status: "off", enabled: false, ringing: null, active: null, activeSince: null });
  }

  private teardown() {
    this.stopRingTone();
    try { this.client?.disconnect(); } catch { /* already down */ }
    this.client = null;
    this.call = null;
  }

  private async loadEngine(): Promise<TelnyxGlobal> {
    const w = window as unknown as { TelnyxWebRTC?: TelnyxGlobal };
    if (w.TelnyxWebRTC?.TelnyxRTC) return w.TelnyxWebRTC;
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "/telnyx-webrtc-2.27.10.js";
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("couldn't load the phone engine"));
      document.head.appendChild(s);
    });
    if (!w.TelnyxWebRTC?.TelnyxRTC) throw new Error("phone engine loaded but missing TelnyxRTC");
    return w.TelnyxWebRTC;
  }

  private async start() {
    if (this.state.status === "ready" || this.state.status === "starting") {
      if (this.client) return;
    }
    this.wantedOn = true;
    this.set({ status: "starting", error: null });

    const engine = await this.loadEngine();
    const { data, error } = await supabase.functions.invoke("softphone-token", { body: { action: "token" } });
    if (error || !data?.token) {
      const msg = (data as { error?: string })?.error ?? error?.message ?? "no login token";
      this.set({ status: "error", error: msg });
      throw new Error(msg);
    }

    if (!this.audioEl) {
      const el = document.createElement("audio");
      el.id = "pegasus-softphone-audio";
      el.autoplay = true;
      el.style.display = "none";
      document.body.appendChild(el);
      this.audioEl = el;
    }

    this.teardown(); // never two clients
    const client = new engine.TelnyxRTC({ login_token: String(data.token) });
    client.remoteElement = this.audioEl;
    this.client = client;

    client.on("telnyx.ready", () => this.set({ status: "ready", error: null, enrolled: true, enabled: true }));
    client.on("telnyx.error", (e) => {
      const msg = String((e as { message?: string })?.message ?? "phone connection error");
      this.set({ status: "error", error: msg });
    });
    client.on("telnyx.socket.close", () => {
      // Dropped socket while we still want to be a phone → quiet retry with a
      // fresh token (they're 24h, but refetching is free and always valid).
      if (!this.wantedOn) return;
      this.set({ status: "error", error: "reconnecting…" });
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => { void this.start().catch(() => undefined); }, 8000);
    });
    client.on("telnyx.notification", (n) => this.onNotification(n as { type?: string; call?: TelnyxCall }));

    await client.connect();
  }

  private onNotification(n: { type?: string; call?: TelnyxCall }) {
    if (n?.type !== "callUpdate" || !n.call) return;
    const call = n.call;
    const st = String(call.state ?? "").toLowerCase();
    const from = call.options?.remoteCallerNumber ?? call.options?.callerNumber ?? "";

    if (st === "ringing") {
      this.call = call;
      this.set({ ringing: { id: call.id, from }, muted: false, held: false });
      this.startRingTone();
      return;
    }
    if (st === "active") {
      this.call = call;
      this.stopRingTone();
      this.set({
        ringing: null,
        active: { id: call.id, from },
        // Coming back from hold must not reset the timer.
        activeSince: this.state.activeSince ?? Date.now(),
        held: false,
      });
      return;
    }
    if (st === "held") {
      this.call = call;
      this.set({ held: true });
      return;
    }
    if (st === "hangup" || st === "destroy" || st === "purge") {
      if (this.call && call.id === this.call.id) this.call = null;
      this.stopRingTone();
      this.set({ ringing: null, active: null, activeSince: null, muted: false, held: false });
    }
  }

  answer() {
    try { this.call?.answer(); } catch (e) { this.set({ error: String((e as Error)?.message ?? e) }); }
  }
  /** Drop OUR leg only — the desk phones keep ringing. */
  decline() {
    this.stopRingTone();
    try { this.call?.hangup(); } catch { /* leg already gone */ }
    this.set({ ringing: null });
  }
  hangup() {
    try { this.call?.hangup(); } catch { /* already down */ }
    this.set({ active: null, activeSince: null, ringing: null, muted: false });
  }
  toggleMute() {
    if (!this.call) return;
    try {
      if (this.state.muted) { this.call.unmuteAudio(); this.set({ muted: false }); }
      else { this.call.muteAudio(); this.set({ muted: true }); }
    } catch { /* mid-teardown */ }
  }
  /** Park the caller (they hear hold music) / bring them back. */
  async toggleHold() {
    if (!this.call) return;
    try {
      if (this.state.held) { await this.call.unhold(); this.set({ held: false }); }
      else { await this.call.hold(); this.set({ held: true }); }
    } catch (e) {
      this.set({ error: String((e as Error)?.message ?? e) });
    }
  }
  /** Who can this call be sent to right now (other browsers + tech cells)? */
  async fetchTargets(): Promise<{ dispatchers: XferTarget[]; techs: XferTarget[] }> {
    const { data, error } = await supabase.functions.invoke("softphone-token", { body: { action: "targets" } });
    if (error) throw new Error(error.message);
    return {
      dispatchers: (data?.dispatchers ?? []) as XferTarget[],
      techs: (data?.techs ?? []) as XferTarget[],
    };
  }
  /** Blind transfer: the server redirects the CALLER leg; Telnyx then drops
   *  our leg, which clears the console by itself. A failed target falls back
   *  to the desk phones server-side — the caller is never stranded. */
  async transfer(callEventId: string, target: XferTarget): Promise<void> {
    const { data, error } = await supabase.functions.invoke("softphone-token", {
      body: { action: "transfer", call_event_id: callEventId, target },
    });
    const msg = (data as { error?: string } | null)?.error ?? error?.message;
    if (msg) throw new Error(msg);
  }

  /** Does this invite belong to a given dock row? (match by caller number) */
  matches(from_e164: string | null | undefined, sc: SoftCall | null): boolean {
    if (!sc) return false;
    const a = last10(from_e164);
    const b = last10(sc.from);
    return a.length === 10 && a === b;
  }

  // Gentle dual-tone ring (WebAudio — no audio asset to ship), 0.9s on/1.1s
  // off, stops on answer/decline/teardown.
  private startRingTone() {
    if (this.ringOsc) return;
    try {
      const Ctx = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
      if (!Ctx) return;
      this.ringCtx = this.ringCtx ?? new Ctx();
      const ctx = this.ringCtx;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(ctx.destination);
      const o1 = ctx.createOscillator(); o1.frequency.value = 440; o1.connect(gain); o1.start();
      const o2 = ctx.createOscillator(); o2.frequency.value = 480; o2.connect(gain); o2.start();
      let on = true;
      gain.gain.setTargetAtTime(0.08, ctx.currentTime, 0.02);
      const iv = setInterval(() => {
        on = !on;
        gain.gain.setTargetAtTime(on ? 0.08 : 0, ctx.currentTime, 0.02);
      }, 1000);
      this.ringOsc = {
        stop: () => {
          clearInterval(iv);
          try { o1.stop(); o2.stop(); gain.disconnect(); } catch { /* done */ }
        },
      };
    } catch { /* no audio — the dock still pulses visually */ }
  }
  private stopRingTone() {
    this.ringOsc?.stop();
    this.ringOsc = null;
  }
}

export const softphone = new SoftphoneManager();

/**
 * The dock's view of the softphone. Pass `allowed` (the calls.softphone
 * capability) — the engine only boots for users who may have a phone.
 */
export function useSoftphone(allowed: boolean): SoftphoneState {
  const state = useSyncExternalStore(softphone.subscribe, softphone.get);
  useEffect(() => {
    if (allowed) softphone.boot();
  }, [allowed]);
  return state;
}
